import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Sparkles, ArrowLeft, AlertCircle } from "lucide-react";
import axios from "axios";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import ChatConsentGate from "../components/ChatConsentGate";

export default function PublicChat() {
  const [msgs, setMsgs] = useState([
    { role: "assistant", text: "Hola, soy el asistente médico de Dr. Dimy Laguerre. Cuéntame qué te sucede y te oriento. ⚕️ Esta orientación no sustituye una consulta médica." }
  ]);
  const [body, setBody] = useState("");
  const [sid, setSid] = useState(null);
  const [remaining, setRemaining] = useState(10);
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    const txt = body.trim();
    if (!txt || busy) return;
    setMsgs((p) => [...p, { role: "user", text: txt }]);
    setBody("");
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/public-chat/message`,
        { message: txt, session_id: sid }
      );
      setSid(data.session_id);
      setRemaining(data.remaining);
      setMsgs((p) => [...p, { role: "assistant", text: data.reply }]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error";
      setMsgs((p) => [...p, { role: "assistant", text: `⚠️ ${msg}`, error: true }]);
    } finally { setBusy(false); }
  };

  return (
    <ChatConsentGate onCancelHref="/">
    <div className="min-h-screen bg-background flex flex-col" data-testid="public-chat-page">
      <header className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Inicio
          </Link>
          <Logo size={32} withText />
        </div>
      </header>

      <main className="flex-1 flex flex-col max-w-3xl w-full mx-auto p-4 sm:p-6 gap-4">
        <Card>
          <CardContent className="p-4 sm:p-5 flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="font-display text-lg">Asistente médico gratuito</div>
              <div className="text-sm text-muted-foreground">
                Resuelve dudas básicas de salud al instante. Hasta {remaining} mensajes/día gratis.
                <span className="ml-1 text-xs opacity-70">Si necesitas diagnóstico, <Link to="/register" className="text-primary underline">reserva una consulta</Link>.</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="public-chat-msgs">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-primary text-primary-foreground rounded-br-sm"
                    : m.error
                      ? "bg-red-50 text-destructive border border-red-200 rounded-bl-sm"
                      : "bg-[#F3F1EC] text-foreground rounded-bl-sm"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start"><div className="bg-[#F3F1EC] rounded-2xl px-4 py-2 text-sm text-muted-foreground">Pensando...</div></div>
            )}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="p-3 border-t border-border flex gap-2">
            <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Describe tus síntomas..." disabled={busy} data-testid="public-chat-input" />
            <Button type="submit" disabled={busy || !body.trim()} className="bg-primary hover:bg-primary/90" data-testid="public-chat-send">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </Card>

        <div className="flex items-start gap-2 text-xs text-muted-foreground bg-[#F3F1EC] rounded-lg p-3" data-testid="public-chat-disclaimer">
          <AlertCircle className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
          <div>
            Este asistente proporciona información orientativa basada en IA (Reglamento UE 2024/1689). <strong>No sustituye</strong> el diagnóstico ni el tratamiento de un médico colegiado. Toda decisión clínica corresponde a un profesional sanitario. En caso de urgencia vital llama al <strong>112</strong>.
          </div>
        </div>
      </main>
    </div>
    </ChatConsentGate>
  );
}
